import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Select from 'react-select'
import moment from 'moment'
import { useAddModalMain } from '../../../contexts/address-modal/AddModal'
import { fetchLsqTransferJob } from '../../../hooks/fetch-hooks/fetch-lsq-transfer-job/fetch-lsq-transfer-job'
import { usePreviewLsqTransfer, useStartLsqTransfer } from '../../../hooks/update-hooks/lsq-transfer/use-lsq-transfer'
import { lsqRowId } from '../helper'
import ConfirmTransferModal from './ConfirmTransferModal'
import {
    OPPORTUNITY_TRANSFER_SCOPE,
    OPPORTUNITY_SCOPE_OPTIONS,
    LSQ_TRANSFER_STATUS,
    TERMINAL_TRANSFER_STATUSES,
    PROSPECT_STAGE_OPTIONS,
    MAIN_STAGE_OPTIONS,
    OPPORTUNITY_STAGE_OPTIONS,
    OPPORTUNITY_STATUS_OPTIONS,
    getSubStageOptions,
    LEAD_TYPE_OPTIONS,
} from '../leadTransferConstants'

// Keep in step with LSQ_TRANSFER_DEFAULT_LEAD_LIMIT in pillup-api/thirdParty/lsq/constants.js.
const DEFAULT_LEAD_LIMIT = 100

const DEFAULT_FILTERS = {
    createdFrom: '',
    createdTo: '',
    modifiedFrom: '',
    modifiedTo: '',
    prospectStage: [],
    mainStage: [],
    subStage: [],
    leadType: [],
    opportunityStatus: [],
    opportunityStage: [],
}

const STATUS_PILL_CLASSES = {
    [LSQ_TRANSFER_STATUS.QUEUED]: 'bg-blue-100 text-blue-800',
    [LSQ_TRANSFER_STATUS.RUNNING]: 'bg-blue-100 text-blue-800',
    [LSQ_TRANSFER_STATUS.COMPLETED]: 'bg-green-100 text-green-800',
    [LSQ_TRANSFER_STATUS.COMPLETED_WITH_ERRORS]: 'bg-amber-100 text-amber-800',
    [LSQ_TRANSFER_STATUS.FAILED]: 'bg-red-100 text-red-800',
}

const agentDisplayName = (row) => [row.FirstName, row.LastName].filter(Boolean).join(' ').trim() || row.EmailAddress || '—'

const toAgentOption = (row) => ({
    value: lsqRowId(row),
    label: agentDisplayName(row),
    email: row.EmailAddress || '',
})

// Render menus in a portal so the sticky action bar and card edges can't clip them.
const MULTI_SELECT_PROPS = {
    isMulti: true,
    closeMenuOnSelect: false,
    menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
    styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
}

const toOptionList = (values) => values.map((value) => ({ value, label: value }))

const dateRangeInvalid = (from, to) => Boolean(from) && Boolean(to) && from > to

const progressPercent = (done, total) => (total > 0 ? Math.round((done / total) * 100) : null)

const formatDateTime = (value) => (value ? moment(value).format('DD MMM YYYY, hh:mm A') : '—')

// Never hand a raw API value to JSX — an object child throws "Objects are not valid as a
// React child" and takes the whole page down.
const asText = (value) => {
    if (value === null || value === undefined || value === '') return '—'
    if (typeof value === 'string' || typeof value === 'number') return String(value)
    return JSON.stringify(value)
}

// The backend now stringifies failure reasons, but jobs created before that fix still sit in
// Redis for up to 7 days holding a raw axios error object — unpack those rather than
// rendering "[object Object]" or crashing.
const failureReasonText = (reason) => {
    if (reason === null || reason === undefined || reason === '') return '—'
    if (typeof reason === 'string') return reason
    if (typeof reason !== 'object') return String(reason)

    const status = reason.status ? `HTTP ${reason.status}` : ''
    const body = reason.data
    let detail = ''

    if (typeof body === 'string') {
        detail = body
    } else if (body && typeof body === 'object') {
        detail = body.ExceptionMessage || body.Message || body.message || JSON.stringify(body)
    } else if (reason.message) {
        detail = String(reason.message)
    }

    return [status, detail].filter(Boolean).join(' — ') || JSON.stringify(reason)
}

const buildSummaryLine = (data, opportunityScope) => {
    const { counts, fromUser, toUser } = data
    const sources = data.fromUsers?.length ? data.fromUsers : [fromUser].filter(Boolean)
    const fromName = sources.map((user) => user.name || user.emailAddress).filter(Boolean).join(', ') || 'the source agent'
    const toName = toUser?.name || toUser?.emailAddress || 'the target agent'

    let line = `Move ${counts.leadsToTransfer} lead${counts.leadsToTransfer === 1 ? '' : 's'}`

    if (opportunityScope !== OPPORTUNITY_TRANSFER_SCOPE.NONE) {
        const scopeLabel = opportunityScope === OPPORTUNITY_TRANSFER_SCOPE.LATEST ? 'latest only' : 'all'
        line += ` and ${counts.opportunitiesToTransfer} opportunit${counts.opportunitiesToTransfer === 1 ? 'y' : 'ies'} (${scopeLabel})`
        if (counts.tasksToTransfer > 0) {
            line += ` plus ${counts.tasksToTransfer} open task${counts.tasksToTransfer === 1 ? '' : 's'}`
        }
    }

    line += ` from ${fromName} to ${toName}.`
    return line
}

const LeadTransferTab = ({ lsqUsers, lsqLoading, lsqError, isActive }) => {
    const { AddopenModal, AddcloseModal } = useAddModalMain()

    const [fromUserIds, setFromUserIds] = useState([])
    const [toUserId, setToUserId] = useState(null)
    const [opportunityScope, setOpportunityScope] = useState(OPPORTUNITY_TRANSFER_SCOPE.LATEST)
    const [transferLeads, setTransferLeads] = useState(true)
    const [transferTasks, setTransferTasks] = useState(true)
    const [limit, setLimit] = useState(DEFAULT_LEAD_LIMIT)
    const [filters, setFilters] = useState(DEFAULT_FILTERS)

    const [preview, setPreview] = useState(null) // { payload, data }
    const [selectedLeadIds, setSelectedLeadIds] = useState([])
    const [formNotice, setFormNotice] = useState('')
    const [showFailures, setShowFailures] = useState(false)
    const [jobId, setJobId] = useState(() => new URLSearchParams(location.search).get('transferJobId'))

    const submittingRef = useRef(false)

    const previewMutation = usePreviewLsqTransfer()
    const startMutation = useStartLsqTransfer()

    const jobPollEnabled = isActive && Boolean(jobId)
    const {
        data: jobResponse,
        isLoading: jobLoading,
        isError: jobIsError,
    } = fetchLsqTransferJob(jobId, jobPollEnabled)

    const job = jobResponse?.data?.data
    const jobEnvelopeFailed = Boolean(jobId) && !jobLoading && (jobIsError || jobResponse?.data?.success === false)
    const jobErrorMessage = typeof jobResponse?.data?.error === 'string' && jobResponse.data.error
        ? jobResponse.data.error
        : 'Could not load this transfer job.'

    useEffect(() => {
        const url = new URL(location)
        if (jobId) {
            url.searchParams.set('transferJobId', jobId)
        } else {
            url.searchParams.delete('transferJobId')
        }
        history.pushState({}, '', url)
    }, [jobId])

    const clearPreview = () => {
        setPreview(null)
        setFormNotice('')
    }

    const handleFromChange = (selected) => {
        const ids = (selected || []).map((option) => option.value)
        setFromUserIds(ids)
        if (toUserId && ids.includes(toUserId)) setToUserId(null)
        clearPreview()
    }

    const handleToChange = (option) => {
        setToUserId(option ? option.value : null)
        clearPreview()
    }

    const handleScopeChange = (value) => {
        setOpportunityScope(value)
        clearPreview()
    }

    const handleTransferLeadsChange = (checked) => {
        setTransferLeads(checked)
        clearPreview()
    }

    const handleTransferTasksChange = (checked) => {
        setTransferTasks(checked)
        clearPreview()
    }

    const handleLimitChange = (value) => {
        setLimit(value)
        clearPreview()
    }

    const updateFilter = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }))
        clearPreview()
    }

    const handleMainStageChange = (values) => {
        setFilters((prev) => {
            const allowed = new Set(getSubStageOptions(values))
            return {
                ...prev,
                mainStage: values,
                subStage: prev.subStage.filter((sub) => allowed.has(sub)),
            }
        })
        clearPreview()
    }

    const handleClearFilters = () => {
        setFilters(DEFAULT_FILTERS)
        clearPreview()
    }

    const resetForm = () => {
        setFromUserIds([])
        setToUserId(null)
        setOpportunityScope(OPPORTUNITY_TRANSFER_SCOPE.LATEST)
        setTransferLeads(true)
        setTransferTasks(true)
        setLimit(DEFAULT_LEAD_LIMIT)
        setFilters(DEFAULT_FILTERS)
        clearPreview()
    }

    const filtersAreSet = Boolean(
        filters.createdFrom || filters.createdTo || filters.modifiedFrom || filters.modifiedTo
        || filters.prospectStage.length || filters.mainStage.length || filters.subStage.length || filters.leadType.length
        || filters.opportunityStatus.length || filters.opportunityStage.length
    )

    const formError = (() => {
        if (fromUserIds.length === 0 || !toUserId) return 'Select at least one source agent and a target agent.'
        if (fromUserIds.includes(toUserId)) return 'The target agent cannot also be a source agent.'
        if (!transferLeads && opportunityScope === OPPORTUNITY_TRANSFER_SCOPE.NONE) {
            return 'Nothing would be transferred — enable lead transfer or choose an opportunity scope.'
        }
        if (dateRangeInvalid(filters.createdFrom, filters.createdTo)) {
            return 'Created from must be on or before Created to.'
        }
        if (dateRangeInvalid(filters.modifiedFrom, filters.modifiedTo)) {
            return 'Modified from must be on or before Modified to.'
        }
        const limitNum = Number(limit)
        if (!Number.isFinite(limitNum) || limitNum < 1 || limitNum > 5000) {
            return 'Max leads must be between 1 and 5000.'
        }
        return ''
    })()

    const buildPayload = () => ({
        fromUserIds,
        toUserId,
        opportunityScope,
        transferLeads,
        transferTasks,
        limit: Number(limit) || DEFAULT_LEAD_LIMIT,
        filters: {
            createdFrom: filters.createdFrom || null,
            createdTo: filters.createdTo || null,
            modifiedFrom: filters.modifiedFrom || null,
            modifiedTo: filters.modifiedTo || null,
            prospectStage: filters.prospectStage,
            mainStage: filters.mainStage,
            subStage: filters.subStage,
            leadType: filters.leadType,
            opportunityStatus: filters.opportunityStatus,
            opportunityStage: filters.opportunityStage,
        },
    })

    const handlePreview = () => {
        if (formError || previewMutation.isLoading) return

        const payload = buildPayload()
        previewMutation.mutate(payload, {
            onSuccess: (response) => {
                if (response?.data?.success) {
                    setPreview({ payload, data: response.data.data })
                    setSelectedLeadIds((response.data.data.leads || []).map((lead) => lead.leadId))
                    setFormNotice('')
                } else {
                    const message = typeof response?.data?.error === 'string' && response.data.error
                        ? response.data.error
                        : ''
                    if (message) setFormNotice(message)
                }
            },
        })
    }

    const handleConfirmTransfer = (transfer) => {
        if (!transfer || submittingRef.current) return
        submittingRef.current = true
        AddcloseModal()

        startMutation.mutate(transfer.payload, {
            onSuccess: (response) => {
                submittingRef.current = false
                if (response?.data?.success && response?.data?.data?.jobId) {
                    setJobId(response.data.data.jobId)
                    setPreview(null)
                    setFormNotice('')
                } else {
                    const message = typeof response?.data?.error === 'string' && response.data.error
                        ? response.data.error
                        : 'Could not start this transfer.'
                    setFormNotice(message)
                }
            },
            onError: () => {
                submittingRef.current = false
            },
        })
    }

    const openTransferModal = (transfer) => {
        AddopenModal(() => (
            <ConfirmTransferModal
                summary={buildSummaryLine(transfer.data, opportunityScope)}
                counts={transfer.data.counts}
                onConfirm={() => handleConfirmTransfer(transfer)}
                onCancel={AddcloseModal}
            />
        ))
    }

    const openConfirmModal = () => {
        if (!preview || selectedLeadIds.length === 0 || previewMutation.isLoading) return

        // Always pin the transfer to the selected lead ids — re-running the filters at start
        // time could pick up leads that appeared after the preview. The re-preview also makes
        // the confirmation counts (opportunities, tasks) describe exactly what will move.
        const payload = { ...preview.payload, leadIds: selectedLeadIds }
        previewMutation.mutate(payload, {
            onSuccess: (response) => {
                if (response?.data?.success) {
                    const transfer = { payload, data: response.data.data }
                    openTransferModal(transfer)
                } else if (typeof response?.data?.error === 'string' && response.data.error) {
                    setFormNotice(response.data.error)
                }
            },
        })
    }

    const toggleLead = (leadId) => {
        setSelectedLeadIds((prev) => (prev.includes(leadId) ? prev.filter((id) => id !== leadId) : [...prev, leadId]))
    }

    const handleStartAnother = () => {
        setJobId(null)
        resetForm()
    }

    const scopeIsNone = opportunityScope === OPPORTUNITY_TRANSFER_SCOPE.NONE

    const fromOptions = (lsqUsers || []).map(toAgentOption).filter((option) => option.value)
    const fromSelectOptions = fromOptions.filter((option) => option.value !== toUserId)
    const toOptions = fromOptions.filter((option) => !fromUserIds.includes(option.value))
    const fromSelected = fromOptions.filter((option) => fromUserIds.includes(option.value))
    const toSelected = toOptions.find((option) => option.value === toUserId) || null

    const subStageOptions = getSubStageOptions(filters.mainStage)

    const checkboxClass = 'w-4 h-4 rounded border-gray-300 text-[#00A48B] focus:ring-[#00A48B]'
    const cardClass = 'bg-white rounded-2xl shadow-soft-xl p-4 font-sans'
    const labelClass = 'block text-sm font-medium text-gray-700 mb-1'

    // ---- Job panel ----
    if (jobId) {
        return (
            <div className="space-y-4">
                {jobLoading && (
                    <div className={cardClass}>
                        <p className="text-gray-600">Loading transfer job…</p>
                    </div>
                )}

                {jobEnvelopeFailed && (
                    <div className={cardClass}>
                        <p className="text-red-600 mb-4">{jobErrorMessage}</p>
                        <button
                            type="button"
                            onClick={handleStartAnother}
                            className="px-4 py-2 text-sm font-medium bg-[#00A48B] text-white-50 rounded-md hover:bg-[#008F79]"
                        >
                            Start another transfer
                        </button>
                    </div>
                )}

                {!jobLoading && !jobEnvelopeFailed && job && (
                    <div className={cardClass}>
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                            <div className="flex items-center gap-3">
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_PILL_CLASSES[job.status] || 'bg-gray-100 text-gray-800'}`}>
                                    {job.status}
                                </span>
                                <span className="text-xs text-gray-500 break-all">Job {job.jobId}</span>
                            </div>
                        </div>

                        {job.stale === true && job.status === LSQ_TRANSFER_STATUS.RUNNING && (
                            <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 px-4 py-3 text-sm">
                                No progress reported in the last 10 minutes — the job may have been interrupted by a server restart. Re-running the same transfer is safe; already-transferred records are skipped.
                            </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                            {[
                                { label: 'Leads', done: job.counts?.leadsDone, total: job.counts?.leadsTotal },
                                { label: 'Opportunities', done: job.counts?.oppsDone, total: job.counts?.oppsTotal },
                                { label: 'Tasks', done: job.counts?.tasksDone, total: job.counts?.tasksTotal },
                            ].map((row) => {
                                const pct = progressPercent(row.done || 0, row.total || 0)
                                return (
                                    <div key={row.label}>
                                        <div className="flex justify-between text-sm text-gray-700 mb-1">
                                            <span>{row.label}</span>
                                            <span>{row.done || 0} / {row.total || 0} ({pct === null ? '—' : `${pct}%`})</span>
                                        </div>
                                        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                                            <div
                                                className="h-2 bg-[#00A48B] rounded-full"
                                                style={{ width: `${pct === null ? 0 : pct}%` }}
                                            />
                                        </div>
                                    </div>
                                )
                            })}
                        </div>

                        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm mb-4">
                            <div><dt className="text-gray-500">Started</dt><dd className="text-gray-900">{formatDateTime(job.startedAt)}</dd></div>
                            <div><dt className="text-gray-500">Updated</dt><dd className="text-gray-900">{formatDateTime(job.updatedAt)}</dd></div>
                            <div><dt className="text-gray-500">Finished</dt><dd className="text-gray-900">{formatDateTime(job.finishedAt)}</dd></div>
                        </dl>

                        <div className="border-t border-gray-100 pt-3">
                            <button
                                type="button"
                                onClick={() => setShowFailures((prev) => !prev)}
                                className="text-sm font-medium text-gray-700 hover:text-gray-900"
                            >
                                {showFailures ? '▼' : '▶'} Failures ({(job.failures || []).length}{(job.failures || []).length >= 100 ? '+' : ''})
                            </button>
                            {showFailures && (job.failures || []).length > 0 && (
                                <table className="table-auto w-[100%] font-sans mt-2">
                                    <thead className="text-left border-b-2 border-gray-100">
                                        <tr>
                                            <th className="font-sans text-left pl-1 py-1">Stage</th>
                                            <th className="font-sans text-left pl-1 py-1">Id</th>
                                            <th className="font-sans text-left pl-1 py-1">Reason</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(job.failures || []).map((failure, idx) => (
                                            <tr key={`${failure.stage}-${failure.id}-${idx}`} className="border-b border-gray-50">
                                                <td className="pl-1 py-1 align-top">{asText(failure.stage)}</td>
                                                <td className="pl-1 py-1 align-top break-all">{asText(failure.id)}</td>
                                                <td className="pl-1 py-1 align-top">{failureReasonText(failure.reason)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                            {(job.failures || []).length >= 100 && (
                                <p className="text-xs text-gray-500 mt-1">Capped at 100 — more failures may have occurred.</p>
                            )}
                        </div>

                        {TERMINAL_TRANSFER_STATUSES.includes(job.status) && (
                            <div className="mt-4 pt-3 border-t border-gray-100">
                                <button
                                    type="button"
                                    onClick={handleStartAnother}
                                    className="px-4 py-2 text-sm font-medium bg-[#00A48B] text-white-50 rounded-md hover:bg-[#008F79]"
                                >
                                    Start another transfer
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        )
    }

    // ---- Form + preview ----
    return (
        <div className="space-y-4">
            <div className={cardClass}>
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Who</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className={labelClass}>Transfer from</label>
                        <Select
                            {...MULTI_SELECT_PROPS}
                            options={fromSelectOptions}
                            value={fromSelected}
                            onChange={handleFromChange}
                            isLoading={lsqLoading}
                            placeholder="Select source agent(s)…"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            {fromSelected.length > 0
                                ? fromSelected.map((option) => option.email || option.label).join(', ')
                                : 'No agent selected.'}
                        </p>
                    </div>
                    <div>
                        <label className={labelClass}>Transfer to</label>
                        <Select
                            options={toOptions}
                            value={toSelected}
                            onChange={handleToChange}
                            isClearable
                            isLoading={lsqLoading}
                            placeholder="Select target agent…"
                        />
                        <p className="text-xs text-gray-500 mt-1">{toSelected?.email || 'No agent selected.'}</p>
                    </div>
                </div>
                {lsqError && (
                    <p className="text-sm text-red-600 mt-2">Could not load LeadSquared agents.</p>
                )}
            </div>

            <div className={cardClass}>
                <h2 className="text-lg font-semibold text-gray-900 mb-3">What to transfer</h2>

                <div className="space-y-3 mb-4">
                    {OPPORTUNITY_SCOPE_OPTIONS.map((option) => (
                        <label key={option.value} className="flex items-start gap-3 cursor-pointer">
                            <input
                                type="radio"
                                name="opportunityScope"
                                className="mt-1 w-4 h-4 text-[#00A48B] focus:ring-[#00A48B]"
                                checked={opportunityScope === option.value}
                                onChange={() => handleScopeChange(option.value)}
                            />
                            <span>
                                <span className="block text-sm font-medium text-gray-800">{option.label}</span>
                                <span className="block text-xs text-gray-500">{option.description}</span>
                            </span>
                        </label>
                    ))}
                </div>

                <div className="space-y-3 mb-4">
                    <label className={`flex items-center gap-2 ${scopeIsNone ? 'opacity-50' : ''}`}>
                        <input
                            type="checkbox"
                            className={checkboxClass}
                            checked={transferTasks}
                            disabled={scopeIsNone}
                            onChange={(e) => handleTransferTasksChange(e.target.checked)}
                        />
                        <span className="text-sm text-gray-700">Also reassign open tasks</span>
                    </label>
                    <p className="text-xs text-gray-500 ml-6">Pending follow-up tasks on the transferred opportunities move to the new owner.</p>

                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            className={checkboxClass}
                            checked={transferLeads}
                            onChange={(e) => handleTransferLeadsChange(e.target.checked)}
                        />
                        <span className="text-sm text-gray-700">Transfer lead ownership</span>
                    </label>
                </div>

                <div>
                    <label className={labelClass}>Max leads</label>
                    <input
                        type="number"
                        min={1}
                        max={5000}
                        value={limit}
                        onChange={(e) => handleLimitChange(e.target.value)}
                        className="w-full sm:w-40 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#00A48B] focus:border-[#00A48B] text-sm"
                    />
                    {fromUserIds.length > 1 && (
                        <p className="text-xs text-gray-500 mt-1">Applies to the transfer as a whole, not per source agent. Sources are read in the order selected.</p>
                    )}
                </div>
            </div>

            <div className={cardClass}>
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
                    {filtersAreSet && (
                        <button type="button" onClick={handleClearFilters} className="text-sm text-[#00A48B] hover:underline">
                            Clear filters
                        </button>
                    )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Created between</p>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={labelClass}>From</label>
                                <input
                                    type="date"
                                    value={filters.createdFrom}
                                    onChange={(e) => updateFilter('createdFrom', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#00A48B] focus:border-[#00A48B] text-sm"
                                />
                            </div>
                            <div>
                                <label className={labelClass}>To</label>
                                <input
                                    type="date"
                                    value={filters.createdTo}
                                    onChange={(e) => updateFilter('createdTo', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#00A48B] focus:border-[#00A48B] text-sm"
                                />
                            </div>
                        </div>
                    </div>
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Modified between</p>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={labelClass}>From</label>
                                <input
                                    type="date"
                                    value={filters.modifiedFrom}
                                    onChange={(e) => updateFilter('modifiedFrom', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#00A48B] focus:border-[#00A48B] text-sm"
                                />
                            </div>
                            <div>
                                <label className={labelClass}>To</label>
                                <input
                                    type="date"
                                    value={filters.modifiedTo}
                                    onChange={(e) => updateFilter('modifiedTo', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#00A48B] focus:border-[#00A48B] text-sm"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className={labelClass}>Lead stage</label>
                        <Select
                            {...MULTI_SELECT_PROPS}
                            options={toOptionList(PROSPECT_STAGE_OPTIONS)}
                            value={toOptionList(filters.prospectStage)}
                            onChange={(selected) => updateFilter('prospectStage', (selected || []).map((o) => o.value))}
                        />
                    </div>
                    <div>
                        <label className={labelClass}>Main stage</label>
                        <Select
                            {...MULTI_SELECT_PROPS}
                            options={toOptionList(MAIN_STAGE_OPTIONS)}
                            value={toOptionList(filters.mainStage)}
                            onChange={(selected) => handleMainStageChange((selected || []).map((o) => o.value))}
                        />
                    </div>
                    <div>
                        <label className={labelClass}>Sub stage</label>
                        <Select
                            {...MULTI_SELECT_PROPS}
                            options={toOptionList(subStageOptions)}
                            value={toOptionList(filters.subStage)}
                            onChange={(selected) => updateFilter('subStage', (selected || []).map((o) => o.value))}
                        />
                    </div>
                    <div>
                        <label className={labelClass}>Lead type</label>
                        <Select
                            {...MULTI_SELECT_PROPS}
                            options={toOptionList(LEAD_TYPE_OPTIONS)}
                            value={toOptionList(filters.leadType)}
                            onChange={(selected) => updateFilter('leadType', (selected || []).map((o) => o.value))}
                        />
                    </div>
                    <div>
                        <label className={labelClass}>Opportunity status</label>
                        <Select
                            {...MULTI_SELECT_PROPS}
                            isDisabled={scopeIsNone}
                            options={toOptionList(OPPORTUNITY_STATUS_OPTIONS)}
                            value={toOptionList(filters.opportunityStatus)}
                            onChange={(selected) => updateFilter('opportunityStatus', (selected || []).map((o) => o.value))}
                        />
                    </div>
                    <div>
                        <label className={labelClass}>Opportunity stage</label>
                        <Select
                            {...MULTI_SELECT_PROPS}
                            isDisabled={scopeIsNone}
                            options={toOptionList(OPPORTUNITY_STAGE_OPTIONS)}
                            value={toOptionList(filters.opportunityStage)}
                            onChange={(selected) => updateFilter('opportunityStage', (selected || []).map((o) => o.value))}
                        />
                    </div>
                </div>
            </div>

            {formError && (
                <p className="text-sm text-red-600 px-1">{formError}</p>
            )}

            {formNotice && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-800 px-4 py-3 text-sm">
                    {formNotice}
                </div>
            )}

            <div className="sticky bottom-0 bg-gray-50 py-3 flex gap-3 z-10">
                <button
                    type="button"
                    onClick={handlePreview}
                    disabled={Boolean(formError) || previewMutation.isLoading}
                    className="px-4 py-2 text-sm font-medium bg-[#00A48B] text-white-50 rounded-md hover:bg-[#008F79] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {previewMutation.isLoading ? 'Previewing…' : 'Preview'}
                </button>
                <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                    Reset
                </button>
            </div>

            {preview && (
                <div className={cardClass}>
                    <h2 className="text-lg font-semibold text-gray-900 mb-3">Preview</h2>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                        {[
                            { label: 'Leads matched', value: preview.data.counts.leadsMatched },
                            { label: 'Leads to transfer', value: preview.data.counts.leadsToTransfer },
                            { label: 'Opportunities to transfer', value: preview.data.counts.opportunitiesToTransfer },
                            { label: 'Tasks to transfer', value: preview.data.counts.tasksToTransfer },
                        ].map((tile) => (
                            <div key={tile.label} className="bg-gray-50 rounded-lg p-3">
                                <div className="text-xs text-gray-500">{tile.label}</div>
                                <div className="text-lg font-semibold text-gray-900">{tile.value}</div>
                            </div>
                        ))}
                    </div>

                    <p className="text-sm text-gray-800 mb-4">{buildSummaryLine(preview.data, opportunityScope)}</p>

                    {(preview.data.warnings || []).length > 0 && (
                        <div className="space-y-2 mb-4">
                            {preview.data.warnings.map((warning, idx) => (
                                <div key={`warning-${idx}`} className="rounded-lg border border-amber-300 bg-amber-50 text-amber-800 px-4 py-3 text-sm">
                                    {warning}
                                </div>
                            ))}
                        </div>
                    )}

                    {preview.data.counts.leadsToTransfer === 0 ? (
                        <p className="text-gray-600 mb-4">Nothing matches these filters.</p>
                    ) : (
                        <>
                            <p className="text-sm font-medium text-gray-700 mb-2">
                                {selectedLeadIds.length} of {preview.data.leads.length} leads selected for transfer
                            </p>
                            <div className="overflow-auto max-h-96 mb-4">
                                <table className="table-auto w-[100%] font-sans">
                                    <thead className="text-left border-b-2 border-gray-50">
                                        <tr>
                                            <th className="font-sans text-left pl-1 py-2 w-8">
                                                <input
                                                    type="checkbox"
                                                    className={checkboxClass}
                                                    checked={selectedLeadIds.length === preview.data.leads.length}
                                                    onChange={(e) => setSelectedLeadIds(e.target.checked ? preview.data.leads.map((lead) => lead.leadId) : [])}
                                                />
                                            </th>
                                            <th className="font-sans text-left pl-1 py-2">Name</th>
                                            <th className="font-sans text-left pl-1 py-2">Phone</th>
                                            {(preview.data.fromUsers || []).length > 1 && (
                                                <th className="font-sans text-left pl-1 py-2">Current owner</th>
                                            )}
                                            <th className="font-sans text-left pl-1 py-2">Lead stage</th>
                                            <th className="font-sans text-left pl-1 py-2">Main / Sub stage</th>
                                            <th className="font-sans text-left pl-1 py-2"># opportunities</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {preview.data.leads.map((lead) => (
                                            <tr key={lead.leadId} className="border-b border-gray-50">
                                                <td className="pl-1 py-2 align-top">
                                                    <input
                                                        type="checkbox"
                                                        className={checkboxClass}
                                                        checked={selectedLeadIds.includes(lead.leadId)}
                                                        onChange={() => toggleLead(lead.leadId)}
                                                    />
                                                </td>
                                                <td className="pl-1 py-2 align-top">{lead.name || '—'}</td>
                                                <td className="pl-1 py-2 align-top">{lead.phone || '—'}</td>
                                                {(preview.data.fromUsers || []).length > 1 && (
                                                    <td className="pl-1 py-2 align-top">{lead.ownerName || '—'}</td>
                                                )}
                                                <td className="pl-1 py-2 align-top">{lead.prospectStage || '—'}</td>
                                                <td className="pl-1 py-2 align-top">{[lead.mainStage, lead.subStage].filter(Boolean).join(' / ') || '—'}</td>
                                                <td className="pl-1 py-2 align-top">{(lead.opportunityIds || []).length}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={openConfirmModal}
                            disabled={selectedLeadIds.length === 0 || previewMutation.isLoading}
                            className="px-4 py-2 text-sm font-medium text-white-50 bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Confirm transfer ({selectedLeadIds.length})
                        </button>
                        <button
                            type="button"
                            onClick={clearPreview}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                            Back to filters
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

LeadTransferTab.propTypes = {
    lsqUsers: PropTypes.array,
    lsqLoading: PropTypes.bool,
    lsqError: PropTypes.bool,
    isActive: PropTypes.bool,
}

LeadTransferTab.defaultProps = {
    lsqUsers: [],
    lsqLoading: false,
    lsqError: false,
    isActive: true,
}

export default LeadTransferTab
